/**
 * 播放页路由辅助工具
 * 处理播放页和播放详情页之间的路由跳转逻辑
 */

import { routeStackManager } from './routeStackManager';
import { logger } from './devUtils';

/**
 * 判断路径是否为播放详情页
 */
export const isPlayDetailPage = (path: string): boolean => {
  return path.startsWith('/playDetail/');
};

/**
 * 判断路径是否为播放页
 */
export const isPlayPage = (path: string): boolean => {
  return path.startsWith('/play/');
};

/**
 * 从URL中移除vid参数并携带来源页面标识
 * @param path 路径字符串,格式如 /playDetail/cid/vid 或 /playDetail/cid
 * @returns 返回移除vid后的路径,格式如 /playDetail/cid
 */
export const removeVidFromPath = (path: string): string => {
  // 如果已经有 sourcePage=play，直接返回
  if (path.includes('sourcePage=play')) {
    return path;
  }

  const match = path.match(/^\/playDetail\/([^/]+)(?:\/([^/]+))?/);
  if (match) {
    const cid = match[1];
    // 检查是否有其他参数
    const hasQuery = path.includes('?');
    return hasQuery
      ? `/playDetail/${cid}${path.substring(path.indexOf('?'))}&sourcePage=play`
      : `/playDetail/${cid}?sourcePage=play`;
  }
  return path;
};

/**
 * 构建播放详情页路径(带sourcePage参数)
 * @param cid 内容ID
 * @param sourcePage 来源页面标识
 * @returns 返回带查询参数的路径
 */
export const buildPlayDetailPath = (cid: string, sourcePage = 'play'): string => {
  return `/playDetail/${cid}?sourcePage=${sourcePage}`;
};

/**
 * 检查并处理播放页进入逻辑
 * 如果上一个页面不是播放详情页,则将播放详情页推入路由栈
 *
 * @param cid 当前播放的内容ID
 * @returns 是否推入了播放详情页
 */
export const handlePlayPageEntry = (cid: string): boolean => {
  try {
    const stack = routeStackManager.getStack();
    const previousPath = stack[stack.length - 1];

    logger.log('[PlayRouteHelper] 当前路由栈:', stack);
    logger.log('[PlayRouteHelper] 上一个页面:', previousPath);

    // 判断上一个页面是否为播放详情页
    if (previousPath && isPlayDetailPage(previousPath)) {
      logger.log('[PlayRouteHelper] 上一个页面是播放详情页,无需推入');
      return false;
    }

    // 上一个页面不是播放详情页,需要推入播放详情页到路由栈
    const playDetailPath = buildPlayDetailPath(cid, 'play');
    logger.log('[PlayRouteHelper] 推入播放详情页到路由栈:', playDetailPath);

    // 在当前播放页之前插入播放详情页
    routeStackManager.insertBeforeCurrent(playDetailPath);

    return true;
  } catch (error) {
    logger.error('[PlayRouteHelper] 处理播放页进入逻辑失败:', error);
    return false;
  }
};

/**
 * 从播放页返回播放详情页时,获取应该跳转的路径(去掉vid)
 *
 * @param cid 内容ID
 * @returns 返回不带vid的播放详情页路径
 */
export const getPlayDetailPathOnBack = (cid: string): string => {
  return `/playDetail/${cid}`;
};
